import React, { useState, useEffect, useContext, useRef } from 'react';
import html2canvas from 'html2canvas'; // Import the rendering library
import jsPDF from 'jspdf';           // Import the PDF generating library
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm , useFieldArray } from 'react-hook-form';
import { Layout } from '../../../../components';
import AppContext from '../../../../contexts/AppContext';
import { handleApi, formatCurrency, formatTwoDecimal, formatMediumDate, capitalize, AudioSpinner,useToast } from '../../../../utils/utils';



// View component for the filterable product input

const ProductSearchInput = ({ products, onSelectProduct, selectedProduct }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  // Add a safeguard to ensure 'products' is a valid array before filtering
  const filteredProducts = (Array.isArray(products) ? products : []).filter(product => {
    if (!product || typeof product.label !== 'string') {
      return false; // Skip any non-valid product objects
    }
    return product.label.toLowerCase().includes(searchTerm.toLowerCase());
  });
  

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  // FINAL FIX: Add a check to ensure selectedProduct is a valid object before trying to access its properties.
  useEffect(() => {
    if (selectedProduct && typeof selectedProduct.product_name === 'string') {
      setSearchTerm(selectedProduct.product_name);
    } else {
      setSearchTerm('');
    }
  }, [selectedProduct]);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <input
        type="text"
        className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2"
        placeholder="Search product..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
      />
      {showDropdown && (
        <ul className="absolute z-10 w-full mt-1 bg-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <li
                key={product.value}
                className="p-2 text-white hover:bg-cyan-200 hover:text-gray-800 cursor-pointer"
                onClick={() => {
                  onSelectProduct(product);
                  setSearchTerm(product.label);
                  setShowDropdown(false);
                }}
              >
                {product.label}
              </li>
            ))
          ) : (
            <li className="p-2 text-gray-400">No products found.</li>
          )}
        </ul>
      )}
    </div>
  );
};


// Icons for the buttons (using simple inline SVG for elegance and no extra dependencies)
const DownloadIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const PrintIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m0 0v2a2 2 0 002 2h4a2 2 0 002-2v-2M9 17h6" />
  </svg>
);

const EmailIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PrintableInvoice = React.forwardRef(({ initialInvoice, companyProfile, formatCurrency: formatCurrencyProp, formatMediumDate: formatMediumDateProp }, ref) => {
    
  // Use destructured props or fallbacks
  const iData = initialInvoice;
  const cData = companyProfile;
  const formatCurrency = formatCurrencyProp;
  const formatMediumDate = formatMediumDateProp;

  // Invoice specific details
  // Calculate 30 days due date, ensuring iData.date is available
  const invoiceDate = iData.date ? new Date(iData.date) : new Date();
  const dueDate = new Date(invoiceDate.getTime() + (14 * 24 * 60 * 60 * 1000));
  const invoiceDueDate = formatMediumDate(dueDate.toISOString());
  
  const documentTitle = 'TAX INVOICE';
  const invoiceNumber = iData?.reference || iData?.id;
  const amountPaid = 0.00; // Mock this value for a fresh invoice
  const balanceDue = iData.totalDiscountedPriceInclusive - amountPaid;
  const sourceRef = iData?.order_id || initialInvoice?.reference; // Use original quotation reference as source

  // Finalized Terms & Conditions (Focused on Payment)
  const paymentInstructions = [
      "1. Payment is due within 14 days of the Invoice Date.",
      "2. Please remit payment to the bank details provided below.",
      "3. Late payments may incur a 5% penalty fee per month."
  ];
  
  // Mock Bank Details
  const bankDetails = {
      name: "PCWorld",
      bank: "Standard Bank",
      branch :"Capital City",
      account: "9100009015261",
  };

  return (
      // High-contrast, max-width 6xl for spacious document. Subtle cyan header border.
      <div ref={ref} className="p-10 mx-auto max-w-6xl bg-white text-gray-900 shadow-2xl border-t-8 border-cyan-600 font-sans text-sm print:shadow-none print:border-t-0">
          
          {/* INVOICE HEADER BLOCK (COMPANY LETTERHEAD) */}
          <header className="flex justify-between items-end pb-8 mb-8 border-b border-gray-300">
              {/* Company Logo and Name */}
              <div className="flex items-start">
                  <img 
                      src={cData.photo_url} 
                      alt={`${cData.name} Logo`} 
                      className="h-16 w-16 mr-4 object-contain rounded-lg" 
                      onError={(e) => { e.target.style.display = 'none' }}
                  />
                  <div className="text-left">
                      <h1 className="text-3xl font-bold text-gray-800">{cData.name.toUpperCase()}</h1>
                      <p className="text-xs text-gray-500 mt-1">{cData.physical_address}</p>
                      <p className="text-xs text-gray-500 mt-1">{cData.city}</p>
                      <p className="text-xs text-cyan-600 font-medium">{cData.email} | {cData.phone_number}</p>
                  </div>
              </div>

              {/* INVOICE TITLE & STATUS */}
              <div className="text-right">
                  <h2 className="text-5xl font-extrabold uppercase text-cyan-600 tracking-wider">
                      {documentTitle}
                  </h2>
                  <p className="text-xl font-semibold text-gray-600 mt-4">
                      Invoice # <span className="text-gray-900 font-extrabold ml-1">{invoiceNumber}</span>
                  </p>
              </div>
          </header>

          {/* BILLING AND INVOICE DATE SECTION */}
          <section className="flex justify-between mb-4 text-xs md:text-sm">
              
              {/* Billed To / Client Details */}
              <div className="w-full md:w-1/2 p-4 border border-gray-200 rounded-lg bg-gray-50 shadow-sm">
                  <p className="font-bold uppercase text-cyan-700 mb-2 border-b border-gray-200 pb-1">Billed To</p>
                  <p className="font-extrabold text-base text-gray-800">{iData?.partners?.name || 'N/A'}</p>
                  <p className="text-gray-700">{iData?.partners?.suburb || 'Address Not Available'}</p>
                  <p className="text-gray-700">{iData?.partners?.city || 'City not available'}</p>
                  <p className="mt-1">Contact: {iData?.partners?.contact_person || 'N/A'}</p>
                  <p>Tel: {iData?.partners?.phone_number || 'N/A'}</p>
              </div>

              {/* Date & Reference Details */}
              <div className="w-full md:w-1/3 text-right pt-4">
                  <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                          <span className="font-semibold text-gray-600">INVOICE DATE:</span>
                          <span className="font-bold text-gray-900">{formatMediumDate(iData?.date)}</span>
                      </div>
                    <div className="flex justify-between items-center my-0">
                            <span className="font-bold text-cyan-700 uppercase tracking-wider">PAYMENT DUE:</span>
                            <span className="inline-block px-3 py-2 text-xs font-extrabold bg-red-600 text-white rounded-md shadow-lg transform hover:scale-105 transition-all">
                                {invoiceDueDate}
                            </span>
                        </div>
                      <div className="flex justify-between my-0">
                          <span className="font-semibold text-gray-600">ORDER REF:</span>
                          <span className="font-bold text-gray-900">{iData?.s_orders?.reference}</span>
                      </div>
                  </div>
              </div>
          </section>

          {/* LINE ITEMS TABLE */}
          <div className="border border-gray-300 rounded-lg overflow-hidden mb-4">
              {/* Table Header */}
              <div className="grid grid-cols-10 gap-2 px-4 py-3 bg-gray-800 text-cyan-400 font-extrabold uppercase text-xs">
                  <div className="col-span-4 text-left">Product / Description</div>
                  <div className="col-span-2 text-right">Unit Price (Excl.)</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-1 text-center">Disc %</div>
                  <div className="col-span-2 text-right">Line Total (Excl.)</div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y divide-gray-200 pb-2">
                  {iData?.transactionLines?.length > 0 ? (
                      iData?.transactionLines.map((field, index) => (
                          <div key={index} className="grid grid-cols-10 items-center px-4 py-1 text-xs md:text-sm hover:bg-cyan-50/50">
                              <div className="col-span-4 text-left pr-2 text-gray-800 font-medium">{field.description || 'N/A'}</div>
                              <div className="col-span-2 text-right text-gray-600">{formatTwoDecimal(field.unitPriceExclusive)}</div>
                              <div className="col-span-1 text-center font-bold text-gray-900">{field?.quantity}</div>
                              <div className="col-span-1 text-center text-red-600">{field?.discount_percentage}</div>
                              <div className="col-span-2 text-right font-extrabold text-gray-900">
                                  {formatTwoDecimal(field.lineDiscountedPriceExclusive)}
                              </div>
                          </div>
                      ))
                  ) : (
                      <p className="p-4 text-center text-gray-500 text-sm">No transaction lines on this invoice.</p>
                  )}
              </div>
          </div>

          {/* SUMMARY & TOTALS / PAYMENT INFO */}
          <div className="flex flex-col md:flex-row justify-between items-start">
              
              {/* Payment Instructions & Notes */}
              <div className="w-full md:w-1/2 pr-0 mx-4 md:mb-0">
                  <h4 className='font-bold text-cyan-700'>PAYMENT INSTRUCTIONS</h4>
                  
                    {/* === UPDATED: ELEGANT, FUTURISTIC LIST STYLE USING UL/LI === */}
                    <ul className="space-y-2 text-xs my-2 list-none p-0">
                        {paymentInstructions.map((term, index) => (
                            // Add all the container styling to the li element
                            <li 
                                key={index} 
                                // Use group and flex utilities on li
                                className="group flex items-center rounded-lg  
                                           transition-all hover:bg-cyan-50/70 hover:border-cyan-400"
                            >
                                {/* This is where we create the custom, numbered bullet */}
                                <div 
                                    className="flex-shrink-0 w-2 h-2 mr-3 flex items-center justify-center 
                                               text-white font-extrabold bg-cyan-600 rounded-full text-[10px] 
                                               shadow-md group-hover:bg-cyan-700"
                                >
                                    {/* Display the list number (index + 1) */}
                                   
                                </div>
                                
                                {/* Instruction Text - Ensures text and badge are vertically aligned */}
                                <span className="text-gray-700 leading-snug">
                                    {/* The term already starts with the number, so we extract the rest of the text */}
                                    {term.substring(term.indexOf(" ") + 1)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    {/* === END UPDATED LIST === */} 
                  <h4 className='font-bold text-cyan-700 mt-4 mb-2'>BANK DETAILS</h4>
                  <div className="text-xs text-gray-700 space-y-1 bg-gray-50 p-1 pb-2 rounded-lg border">
                    <p>Account Name: <span className='font-semibold'>{bankDetails.name}</span></p>
                    <p>Bank Name: <span className='font-semibold'>{bankDetails.bank}</span></p>
                    <p>Branch: <span className='font-semibold'>{bankDetails.branch}</span></p>
                    <p>Account No: <span className='font-semibold text-cyan-700'>{bankDetails.account}</span></p>
                    {/* <p>SWIFT/BIC: <span className='font-semibold'>{bankDetails.swift}</span></p> */}
                  </div>

                  {/* <h4 className='font-semibold text-gray-700 mt-4'>DESCRIPTION</h4>
                  <p className="whitespace-pre-wrap text-gray-600 text-xs italic bg-gray-50 p-3 rounded-lg border">
                    {iData.description || 'No detailed description provided for this invoice.'}
                </p> */}
              </div>
              
              {/* Totals Calculation */}
              <div className="w-full md:w-auto max-w-sm">
                  <div className="text-sm text-gray-700 font-bold space-y-1 bg-gray-50 p-1 rounded-lg border-cyan-700 border-2">
                      {/* Subtotal */}
                      <div className="flex justify-between py-1">
                          <div>Subtotal (Excl. Tax):</div>
                          <div className="font-semibold text-gray-800">{formatCurrency(iData.totalDiscountedPriceExclusive)}</div>
                      </div>
                      {/* Discount */}
                      <div className="flex justify-between py-1">
                          <div>Total Discount:</div>
                          <div className="font-semibold text-red-600">- {formatCurrency(iData.totalDiscountInclusive)}</div>
                      </div>
                      
                      {/* Tax */}
                      <div className="flex justify-between py-2 border-t border-gray-200">
                          <div>Sales Tax :</div>
                          <div className="font-bold text-gray-800">{formatCurrency(iData.totalTaxAfterDiscounted)}</div>
                      </div>
                      {/* Grand Total */}
                      <div className="flex justify-between pt-2 border-t-2 border-gray-600 text-base font-extrabold text-gray-900">
                          <div>INVOICE TOTAL:</div>
                          <div>{formatCurrency(iData.totalDiscountedPriceInclusive)}</div>
                      </div>
                      {/* Amount Paid (Mock) */}
                      <div className="flex justify-between py-1 font-semibold text-green-700">
                          <div>Amount Paid:</div>
                          <div>{formatCurrency(amountPaid)}</div>
                      </div>
                  </div>
                  
                  {/* BALANCE DUE - HIGHEST EMPHASIS */}
                  <div className='flex justify-between bg-cyan-600 text-white rounded-lg font-extrabold text-2xl mt-4 p-4 shadow-xl'> 
                      <div>BALANCE DUE:</div>
                      <div>{formatCurrency(balanceDue)}</div>
                  </div>
              </div>
          </div>

          {/* Footer / Thank You */}
          <footer className="mt-20 pt-8 text-center text-xs text-gray-600 border-t border-gray-300">
              <p className="mb-8 italic text-sm font-medium text-gray-700">
                  Thank you for your business. We look forward to your prompt payment!
              </p>

          </footer>

      </div>
  );
});

// You would typically export the main component for use in the parent file
// export default PrintableInvoice; 
const getStatusStyles = (status) => {
  let text = status || 'Draft';
  let pillClasses = 'bg-gray-200 text-gray-700';
  return { text, pillClasses };
};

const ViewSalesInvoice = () => {
  const location = useLocation()
  const navigate = useNavigate();
  const { initialInvoice } = location.state || {};
  console.log('intial qoue',initialInvoice)
  
//   const [invoice, setInvoice] = useState(initialFormState);

  const [transactionLines, setTransactionLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [formattedProducts, setFormattedProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formattedCustomers, setFormattedCustomers] = useState([]);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null); //state to hold customer details
  const { userData, currentColor, actionCount,companyProfile,setActionCount} = useContext(AppContext);
  console.log('company Profile',companyProfile)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); // NEW STATE
  const { showToast, toastMessage } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false); // State for the reset confirmation modal
  const [focusedIndex, setFocusedIndex] = useState(null);
  // const invoiceRef = useRef(null); // Ref to the invoice content for PDF generation
  const quantityRefs = useRef([]);    // a ref for each quantity input field to manage focus

  const TransactionLineHeader = () => {
    return (
      <div className="grid grid-cols-6 gap-2 p-2 border-b rounded-lg bg-gray-100 text-gray-800 font-semibold text-xs md:text-xs">
        <div className="col-span-2 text-left">Product Description</div>
        <div className="col-span-1 text-center">Price</div>
        <div className="col-span-1 text-center">Qty</div>
        <div className="col-span-1 text-center">Disc %</div>
        <div className="col-span-1 text-right">Total</div>
      </div>
    );
  };

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return {
          text: 'APPROVED',
          pillClasses: 'bg-emerald-700/50 text-emerald-300 ring-emerald-500/30',
        };
      case 'invoiced':
        return {
          text: 'INVOICED',
          pillClasses: 'bg-violet-700/50 text-violet-300 ring-violet-500/30',
        };
      case 'draft':
        return {
          text: 'DRAFT',
          pillClasses: 'bg-slate-700/50 text-slate-300 ring-slate-500/30',
        };
      case 'cancelled':
        return {
          text: 'CANCELLED',
          pillClasses: 'bg-rose-700/50 text-rose-300 ring-rose-500/30',
        };
      default:
        return {
          text: status?.toUpperCase() || 'UNKNOWN',
          pillClasses: 'bg-gray-700/50 text-gray-400 ring-gray-500/30',
        };
    }
  };
  
  // ... inside your component
  const status = initialInvoice?.state;
  const { text, pillClasses } = getStatusStyles(status);
 
  // Inside the ViewSalesInvoice component...

const invoiceRef = useRef(null); // Ref to the invoice content for PDF generation
// ... (other state/refs)

const handleDownload = async () => {
  // 1. Trigger the rendering of the PrintableInvoice component off-screen
  setIsGeneratingPdf(true); 

  // Wait for the state update and render cycle to complete
  // Use a small delay to ensure the component is fully rendered in the DOM
  await new Promise(resolve => setTimeout(resolve, 50)); 
  
  const input = invoiceRef.current; 
  
  if (!input) {
      // This should now be impossible, but acts as a safeguard
      setIsGeneratingPdf(false);
      showToast("Error: Printable invoice layout not rendered.", 'error');
      return;
  }

  setIsLoading(true);

  try {
      // 2. Perform the high-quality PDF generation
      const canvas = await html2canvas(input, {
          scale: 3, 
          useCORS: true, 
          logging: false,
          backgroundColor: '#ffffff', // Explicitly white background
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0); 
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      while (heightLeft >= 0) {
        let position = heightLeft - imgHeight;
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
        if (heightLeft >= 0) { pdf.addPage();}
      }

      const filename = `${initialInvoice?.partners?.name || 'Invoice'}-${initialInvoice.id || new Date().getTime()}.pdf`;
      pdf.save(filename);
      
      showToast("Formal invoice downloaded successfully!", 'success');

  } catch (error) {
      console.error("PDF generation failed:", error);
      showToast("Failed to generate PDF. Check console for details.", 'error');
  } finally {
      setIsLoading(false);
      // 3. Hide the component immediately after the download process is done
      setIsGeneratingPdf(false); 
  }
};

const handleAction = (action) => {
    if (action === 'download') {
        handleDownload(); // Call the new function!
    } else if (action === 'print') {
        window.print();
    } else {
        showToast(`Simulated action: ${action}`, 'success');
    }
};
   return (
    <Layout>
      {/* 1. CONDITIONAL OFF-SCREEN RENDERING */}
      {isGeneratingPdf && (
          <div 
              // This is the key styling: renders the content way off-screen
              className="absolute -left-[9999px] -top-[9999px] opacity-0" 
              style={{ width: '800px' }} // Set a fixed width for consistent PDF output
          >
              <PrintableInvoice
                  ref={invoiceRef} // Attach the ref here
                  initialInvoice={initialInvoice}
                  companyProfile={companyProfile} // Make sure COMPANY_PROFILE is defined
                  getStatusStyles={getStatusStyles}
                  formatCurrency={formatCurrency}
                  formatMediumDate={formatMediumDate}
              />
          </div>
      )}

      <div className="min-h-screen text-white p-2 sm:p-4">
        {/* Toast Message Display */}
        {toastMessage.show && (
          <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md z-50
            ${toastMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {toastMessage.message}
          </div>
        )}
        
        {/* Header and Back Button */}
        <div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center text-xl sm:text-2xl">
            INVOICE PREVIEW
          </h1>
          {/* Action Button Group (NEW) */}
          {initialInvoice?.state === 'approved' && (
            <div className="flex gap-1 sm:gap-2 mr-2"> 
              <button
                onClick={() => handleAction('download')}
                className="p-2 rounded-full text-cyan-300 bg-gray-700/50 hover:bg-cyan-500 hover:text-gray-900 transition-all duration-300 shadow-md border border-cyan-500/50"
                aria-label="Download Invoice"
                title="Download PDF"
              >
                <DownloadIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <button
                onClick={() => handleAction('print')}
                className="p-2 rounded-full text-cyan-300 bg-gray-700/50 hover:bg-cyan-500 hover:text-gray-900 transition-all duration-300 shadow-md border border-cyan-500/50"
                aria-label="Print Invoice"
                title="Print"
              >
                <PrintIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <button
                onClick={() => handleAction('email')}
                className="p-2 rounded-full text-cyan-300 bg-gray-700/50 hover:bg-cyan-500 hover:text-gray-900 transition-all duration-300 shadow-md border border-cyan-500/50"
                aria-label="Email Invoice"
                title="Email"
              >
                <EmailIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300 shadow-md"
              aria-label="Go back"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          </div>
        </div>

            {/* Main Content Area */}
            <div 
          className="bg-gray-800/90 backdrop-blur-sm rounded-3xl border border-cyan-700/50 shadow-2xl p-4 sm:p-6 mx-auto max-w-4xl"
          ref={invoiceRef} // <--- THIS IS THE MISSING PIECE!
        > 
          {isLoading ? (
            <div className="flex flex-col items-center py-10">
              <AudioSpinner />
              <p className="mt-4 text-cyan-300 text-base">Loading Invoice Data. Please wait...</p>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Customer Info Card - View Only */}
                <div className="bg-gray-700/70 border border-cyan-500/30 p-4 rounded-xl shadow-lg">
                  <p className="font-extrabold text-cyan-200 text-center mb-3 uppercase text-lg border-b border-cyan-500/50 pb-2">Customer Details</p>
                  <div className="flex flex-col gap-3 text-sm">
                    {/* Customer Name (Replaces Select Field) */}
                    <div className="flex flex-col">
                      <label className="font-medium text-cyan-300 mb-1">Customer Name</label>
                      <div className="w-full bg-gray-700 text-white font-bold border-b border-cyan-400 py-1 px-1">
                        {initialInvoice?.partners?.name || 'N/A'}
                      </div>
                    </div>

                
                      <>
                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Telephone</label>
                          <span className="text-white font-medium text-right">{initialInvoice?.partners?.phone_number || 'NA'}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Contact Person</label>
                          <span className="text-white font-medium text-right">{initialInvoice?.partners?.contact_person || 'NA'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Email Address</label>
                          <span className="text-white font-medium text-right">{initialInvoice?.partners?.email || 'NA'}</span>
                        </div>
                      </>
                  
                  </div>
                </div>

                {/* Invoice Info Card - View Only */}
                <div className="bg-gray-700/70 border border-cyan-500/30 p-4 rounded-xl shadow-lg">
                  <p className="font-extrabold text-cyan-200 text-center mb-3 uppercase text-lg border-b border-cyan-500/50 pb-2">Invoice Details</p>
                  <div className="flex flex-col gap-3 text-sm">
                    {initialInvoice.s_orders.description ? (
                      // If description exists, show the description
                      <p className="w-full bg-gray-700 text-white border-b border-cyan-400 p-1 min-h-[40px] whitespace-pre-wrap">
                        {initialInvoice?.s_orders?.description}
                      </p>
                    ) : (
                      // Otherwise, show the placeholder
                      <div className="w-full bg-gray-700 text-white border-b border-cyan-400 p-1 min-h-[40px] whitespace-pre-wrap">
                        {'No description provided.'}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <label className="font-medium text-cyan-300">Reference #</label>
                      <span className="text-white font-medium text-right">{initialInvoice?.reference || initialInvoice?.id || 'NA'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <label className="font-medium text-cyan-300">Sales Order #</label>
                      <span className="text-white font-medium text-right">{initialInvoice?.s_orders?.reference ||  'NA'}</span>
                    </div>
                          
                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Invoice Date</label>
                          <span className="text-white font-medium text-right">{formatMediumDate(initialInvoice?.date) || 'NA'}</span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                          <label className="font-light text-sm tracking-wider text-cyan-300 uppercase">
                            Status
                          </label>
                          
                          <span 
                            className={`
                              inline-flex 
                              items-center 
                              px-3 py-1 
                              text-xs 
                              font-semibold 
                              rounded-full 
                              uppercase 
                              tracking-widest 
                              shadow-lg 
                              ring-1
                              transition-colors 
                              duration-300
                              ${pillClasses}
                            `}
                          >
                            {text}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

              {/* Transaction Lines Section - View Only */}
            
                <div className="bg-gray-700/70 border border-cyan-500/30 rounded-xl shadow-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-10 gap-2 p-3 bg-cyan-900 text-cyan-200 font-extrabold uppercase text-xs sm:text-sm md:text-base">
                    <div className="col-span-4 text-left">Product / Description</div>
                    <div className="col-span-2 text-center">Unit Price</div>
                    <div className="col-span-1 text-center">Qty</div>
                    <div className="col-span-1 text-center">Disc %</div>
                    <div className="col-span-2 text-right">Line Total</div>
                  </div>
                  
                  {/* Table Body - Map through lines for viewing */}
                  <div className="p-1">
                    {/* Assuming the `fields` array now holds the read-only data */}
                    {initialInvoice?.transactionLines?.length > 0 ? (
                      initialInvoice?.transactionLines.map((field, index) => {
                        // Watch for line data to display
                        // const line = watch(`transactionLines.${index}`);
                        
                   

                        return (
                          <div key={field.id} className="grid grid-cols-10 items-center px-3 py-2 border-b border-gray-600 hover:bg-gray-700 transition-colors duration-200 text-sm">
                            {/* Product / Description */}
                            <div className="col-span-4 text-left text-white font-medium pr-2">
                              {field.description || 'N/A'}
                            </div>
                            {/* Price */}
                          

                            <div className="col-span-2 text-center text-cyan-300">
                              {formatTwoDecimal(field.unitPriceExclusive)}
                            </div>
                            {/* Quantity */}
                            <div className="col-span-1 text-center text-white">
                              {field?.quantity}
                            </div>
                            {/* Discount */}
                            <div className="col-span-1 text-center text-red-400">
                              {field?.discount_percentage}
                            </div>
                            {/* Total (Net of Tax) */}
                            <div className="col-span-2 text-right text-white font-bold">
                              {formatTwoDecimal(field.lineDiscountedPriceExclusive)}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="p-6 text-center text-cyan-400/70 text-base">No transaction lines on this invoice.</p>
                    )}
                  </div>
                </div>
              


              {/* Invoice Summary - View Only */}
            
                <div className="bg-gray-700/70 border border-cyan-500/30 p-4 rounded-xl shadow-lg">
                  <div className="font-extrabold text-xl sm:text-2xl text-cyan-200 mb-3 border-b border-cyan-500/50 pb-2">Summary</div>
                  <div className="space-y-2">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Subtotal (Exc Tax):</div>
                      <div className="text-base font-semibold text-white">{formatCurrency(initialInvoice.totalDiscountedPriceExclusive)}</div>
                    </div>
                    {/* Discount */}
                    {/* <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Total Discount:</div>
                      <div className="text-base font-semibold text-red-400">{formatCurrency(initialInvoice.totalDiscountExclusive)}</div>
                    </div> */}
                    {/* Subtotal After Discount */}
                    {/* <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Subtotal After Discount (Excl. Tax):</div>
                      <div className="text-base font-semibold text-white">{}</div>
                    </div> */}
                    {/* Tax */}
                    <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Total Tax:</div>
                      <div className="text-base font-semibold text-white">{formatCurrency(initialInvoice.totalTaxAfterDiscounted)}</div>
                    </div>
                  </div>
                  {/* Grand Total */}
                  <div className="flex justify-between items-center pt-4 font-extrabold text-xl text-cyan-100">
                    <div className="text-lg">Grand Total (Incl. Tax):</div>
                    <div className="text-lg">{formatCurrency(initialInvoice.totalDiscountedPriceInclusive)}</div>
                  </div>
                  <div className="flex justify-between items-center pt-4 font-extrabold text-sm text-cyan-100">
                    <div className="text-sm">Total Discount:</div>
                    <div className="text-sm text-cyan-400">{formatCurrency(initialInvoice.totalDiscountInclusive)}</div>
                  </div>
                </div>
              
            </div>
          )}

          {/* --- REVISED SUBTLE PAYMENT HISTORY SECTION (WITH TOTAL PAID) --- */}
          {initialInvoice?.payments?.length > 0 && (
            <div className="mt-10 pt-4 border-t border-gray-500/50">
              {/* Title Header */}
              <h3 className="text-xl font-bold uppercase text-gray-400 tracking-wider mb-3">Payment Installments</h3>
              <div className="border border-gray-700/50 rounded-lg overflow-hidden shadow-inner">
                {/* Table Header */}
                <div className="grid grid-cols-6 gap-4 px-4 py-2 bg-gray-700 text-gray-400 font-semibold uppercase text-xs">
                  <div className="col-span-2 text-left">Date Paid</div>
                  <div className="col-span-2 text-left">Method</div>
                  <div className="col-span-2 text-right">Amount Paid</div>
                </div>

                {/* Table Body (Payment Rows) */}
                <div className="divide-y divide-gray-700">
                  {initialInvoice.payments.map((payment, index) => (
                    <div 
                      key={index} 
                      className="grid grid-cols-6 gap-4 items-center px-4 py-2 text-xs bg-gray-800/50 hover:bg-gray-700 transition-colors duration-150"
                    >
                      {/* Date Paid */}
                      <div className="col-span-2 text-left font-medium text-gray-300">{formatMediumDate(payment.date_paid)}</div>
                      {/* Payment Method */}
                      <div className="col-span-2 text-left text-cyan-400 italic">{capitalize(payment.method)}</div>
                      {/* Amount Paid */}
                      <div className="col-span-2 text-right font-semibold text-green-400">{formatCurrency(payment.amount_paid)}</div>
                    </div>
                  ))}
                </div>

                {/* Payment Summary Totals */}
                <div className="divide-y divide-gray-700">
                  {/* --- TOTAL PAID FIELD --- */}
                  <div className="grid grid-cols-6 gap-4 px-4 py-2 text-sm font-bold bg-gray-900/40">
                    <div className="col-span-4 text-left uppercase text-gray-300 tracking-wider">Total Paid:</div>
                    <div className="col-span-2 text-right text-green-400">{formatCurrency(initialInvoice.total_paid_calculated || 0)} </div>
                  </div>
                
                  {/* Outstanding Balance Row */}
                  <div className="grid grid-cols-6 gap-4 px-4 py-3 bg-gray-900/50 font-extrabold text-sm">
                    <div className="col-span-4 text-left uppercase text-red-400 tracking-wider">Outstanding Balance Due:</div>
                    <div className="col-span-2 text-right text-red-500 text-base">{formatCurrency(initialInvoice.amount_due || 0)} </div>
                  </div>
                </div> 
              </div>
            </div>
          )}
          {/* ---  PAYMENT HISTORY SECTION --- */}

          {/* Footer - Only a Back button is needed */}
          <div className="flex justify-center mt-8">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 rounded-xl font-bold text-gray-900 bg-cyan-400 hover:bg-cyan-300 transition-all duration-300 shadow-xl tracking-wider uppercase"
            >
              Back to List
            </button>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default ViewSalesInvoice;
